import { AsyncPipe } from "@angular/common";
import { Component, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChanges, ViewChild, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { Router, RouterLink } from "@angular/router";
import { SvgIconRegistry, ThemeMode, ThemeService } from "@dffedb/util";
import { mdiCheck, mdiChevronDown, mdiChevronRight, mdiMessageProcessingOutline, mdiPalette } from "@mdi/js";

import { TeknikerPasswordCapabilityService } from "../../auth/tekniker-password-capability.service";
import { Bruger } from "../../bruger";
import { ForsyningIndstillinger } from "../../forsyning-indstillinger";
import { UmbracoIndstillinger } from "../../umbraco-indstillinger";
import { FeatureHighlightDirective } from "../feature-highlight/feature-highlight.directive";
import { FeedbackDialogComponent } from "../top-navigation/feedback-dialog/feedback-dialog.component";
import { Menu } from "../top-navigation/menu/menu";
import { UserInfo, UserInfoData } from "../top-navigation/user-info/user-info";

export interface ServiceProUserMenuNavItem {
    label: string;
    icon: string;
    route: string;
    id?: string;
}

@Component({
    selector: "service-pro-user-menu",
    imports: [AsyncPipe, Menu, UserInfo, MatIcon, RouterLink, FeatureHighlightDirective],
    templateUrl: "./service-pro-user-menu.html",
    styleUrl: "./service-pro-user-menu.scss"
})
export class ServiceProUserMenu implements OnInit, OnChanges {
    @Input()
    public indstillinger: UmbracoIndstillinger | null = null;

    @Input()
    public aktuelBruger: Bruger | null = null;

    @Input()
    public aktuelForsyning: ForsyningIndstillinger | null = null;

    /** Ekstra nav-links (fx Min side / Søg når top-header er skjult). */
    @Input()
    public extraNavItems: ServiceProUserMenuNavItem[] = [];

    /** Overlay på kort: kompakt knap uden værk-navn. */
    @Input()
    public mapOverlay = false;

    @ViewChild("burgerButton") private readonly burgerButton!: ElementRef<HTMLButtonElement>;

    public showMenu = false;
    public showThemeSubmenu = false;
    public userInfoData: UserInfoData | null = null;

    private readonly themeService = inject(ThemeService);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);
    public readonly themeOptions = this.themeService.getThemeOptions();
    public readonly activeTheme = this.themeService.activeTheme;
    public readonly teknikerPasswordFeaturesSupported$ = inject(TeknikerPasswordCapabilityService).supportsTeknikerPasswordFeatures$();

    constructor(
        private readonly elementRef: ElementRef,
        svgIconRegistry: SvgIconRegistry
    ) {
        svgIconRegistry.registerIcon(mdiPalette, "menu-theme");
        svgIconRegistry.registerIcon(mdiChevronRight, "chevron-right");
        svgIconRegistry.registerIcon(mdiChevronDown, "chevron-down");
        svgIconRegistry.registerIcon(mdiCheck, "check");
        svgIconRegistry.registerIcon(mdiMessageProcessingOutline, "feedback");
    }

    public ngOnInit(): void {
        this.rebuildUserInfoData();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes["indstillinger"] || changes["aktuelBruger"] || changes["aktuelForsyning"]) {
            this.rebuildUserInfoData();
        }
    }

    @HostListener("document:click", ["$event"])
    public onDocumentClick(event: MouseEvent): void {
        if (this.showMenu && !this.elementRef.nativeElement.contains(event.target)) {
            this.showMenu = false;
            this.showThemeSubmenu = false;
        }
    }

    @HostListener("document:keydown.escape")
    public onEscape(): void {
        if (this.showMenu) {
            this.closeAndFocus();
        }
    }

    public toggleMenu(): void {
        this.showMenu = !this.showMenu;
        if (!this.showMenu) {
            this.showThemeSubmenu = false;
        }
        if (this.showMenu) {
            setTimeout(() => {
                const firstItem = document.querySelector<HTMLElement>('#user-dropdown-menu [role="menuitem"]');
                firstItem?.focus();
            });
        }
    }

    public closeAndFocus(): void {
        this.showMenu = false;
        this.showThemeSubmenu = false;
        setTimeout(() => this.burgerButton?.nativeElement?.focus());
    }

    public toggleThemeSubmenu(): void {
        this.showThemeSubmenu = !this.showThemeSubmenu;
    }

    public selectTheme(mode: ThemeMode): void {
        this.themeService.setTheme(mode, true);
    }

    public onSkiftAdgangskode(): void {
        this.showMenu = false;
        this.showThemeSubmenu = false;
        void this.router.navigateByUrl("/skift-kodeord");
    }

    public openFeedback(): void {
        this.showMenu = false;
        this.showThemeSubmenu = false;
        this.dialog.open(FeedbackDialogComponent, {
            width: "100%",
            maxWidth: "768px",
            data: {
                aktuelBruger: this.aktuelBruger,
                indstillinger: this.indstillinger
            }
        });
    }

    private rebuildUserInfoData(): void {
        const initialer = (this.aktuelBruger?.initialer || "").trim();
        this.userInfoData = {
            user: {
                initialer,
                name: this.aktuelBruger ? this.aktuelBruger.navn : "",
                email: this.aktuelBruger ? this.aktuelBruger.email : ""
            },
            works: {
                name: this.aktuelForsyning?.forsyningNavn || (this.indstillinger ? this.indstillinger.firmaNavn : ""),
                phone: this.indstillinger?.forsyningWeb?.firmaInfo?.telefon ? this.indstillinger.forsyningWeb.firmaInfo.telefon : ""
            }
        };
    }
}
